import React, { useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { API } from '../../controllers/bulkActionStatus';
import Logger, { LOG_LEVELS } from '../../utils/logger';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import ApplicationMessageForBulkAction from '../bulkActions/ApplicationMessageForBulkAction';
import BulkTransactionErrorModal from '../bulkActions/BulkTransactionErrorModal';
import { OperationTypes } from '../../utils/enums';

const log = new Logger('IMPORT_EXCHANGE_RATES_STATUS_DATA', LOG_LEVELS.DEBUG);
const PAGE_NAME = "IMPORT_EXCHANGE_RATES_STATUS_DATA_PAGE";

export const ImportExchangeRatesStatusData = ({ dispatch, operations, loading, showModal, setShowModal, selectedOperation, setSelectedOperation, tenantId, userId, onClearInitialMessage }) => {
    const [refreshCounter, setRefreshCounter] = useState(0);
    const operationsArray = Array.isArray(operations) ? operations : [];

    useEffect(() => {
        return () => {
            dispatch({ type: 'CLEAR_OPERATIONS' });
            setRefreshCounter(0);
        };
    }, []);

    const closeModal = () => {
        setShowModal(false);
        setSelectedOperation(null);
    };

    const getOperationName = (operationType) => {
        return operationType?.includes('ImportExchangeRates') 
            ? 'Import exchange rates' 
            : 'Unknown';
    };

    const createMessages = (operation) => {
        if (!operation?.status) {
            log.debug("Operation or operation.status is missing");
            return [];
        }

        const messageConfigMap = {
            'Failed': {
                languageString: "inAppMessage_App_Error_176",
                extendedLanguageString: "inAppMessage_App_Error_176_extended"
            },
            'Success': {
                languageString: "inAppMessage_Success_38",
                extendedLanguageString: "inAppMessage_Success_38_extended"
            },
            'InProgress': {
                languageString: "inAppMessage_Info_12",
                extendedLanguageString: "inAppMessage_Info_12_extended"
            }
        };

        const messageStrings = messageConfigMap[operation.status];
        
        if (!messageStrings) {
            log.debug(`Unhandled operation state: ${operation.status}`);
            return [];
        }

        const operationName = getOperationName(operation.operationType);
        const messageConfig = {
            ...messageStrings,
            jobName: operation.jobName,
            operationName: operationName
        };

        return [{
            message: messageConfig,
            args: [operationName]
        }];
    };

    const getOperationTypeIds = () => [
        OperationTypes.ImportExchangeRates
    ];

    const getMessageType = (status) => {
        const messageTypeMap = {
            'Success': 'applicationSuccess',
            'InProgress': 'applicationInfo',
            'Failed': 'applicationError'
        };
        return messageTypeMap[status] || null;
    };

    const handleOperationStatus = useCallback(() => {
        // Clear the initial message when checking status
        if (onClearInitialMessage) {
            onClearInitialMessage();
        }
        
        setRefreshCounter(prev => prev + 1);
        const operationTypeIds = getOperationTypeIds();
        dispatch(API.getOperations(tenantId, userId, operationTypeIds,
            (response) => {
                log.debug("Success fetching Operations Status");
            },
            (error) => {
                log.debug("Error fetching operations : " + error)
            }
        ));
    }, [dispatch, setRefreshCounter, tenantId, userId, onClearInitialMessage]);

    return (
        <div>
            <button
                name="CheckStatus"
                className={'secondary mb20'}
                disabled={loading}
                data-testid='check-status-button'
                onClick={handleOperationStatus}
            >
                <LanguageStringResolver
                    string={'Button_ExchangeRates_CheckRetrieveOnlineStatus'}
                    tooltip={'Button_ExchangeRates_CheckRetrieveOnlineStatus_Extended'}
                />
            </button>
            {/* Show no transactions message if operations is empty */}

            {refreshCounter > 0 && (!operationsArray || operationsArray.length === 0) && (
                <ApplicationMessageForBulkAction
                    key={`no-transactions-${refreshCounter}`}
                    messages={[{
                        message: {
                            languageString: 'inAppMessage_Info_10',
                            extendedLanguageString: "inAppMessage_Info_10_extended"
                        }
                    }]}
                    messageType="applicationInfo"
                    scopes={[PAGE_NAME]}
                    disableDismiss={false}
                />
            )}
            {operationsArray?.map((operation) => {
                const messageType = getMessageType(operation.status);
                
                if (!messageType) {
                    return null;
                }

                return (
                    <ApplicationMessageForBulkAction
                        key={`${operation.operationId}-${refreshCounter}`}
                        messages={createMessages(operation)}
                        messageType={messageType}
                        scopes={[PAGE_NAME]}
                        disableDismiss={false}
                    />
                );
            })
            }
            {selectedOperation && <BulkTransactionErrorModal operation={selectedOperation} showModal={showModal} onClose={closeModal} />}
        </div>

    );
};


ImportExchangeRatesStatusData.defaultProps = {
    operations: [],
    loading: false,
    selectedOperation: null,
    onClearInitialMessage: null
};

const mapStateToProps = (state) => ({
    operations: Array.isArray(state.bulkActionStatus?.operations)
        ? state.bulkActionStatus.operations
        : [],
    loading: state.bulkActionStatus?.loading || false,
    error: state.bulkActionStatus?.error || null
});

export default connect(mapStateToProps)(ImportExchangeRatesStatusData);

